import { useEffect, useMemo, useRef, useState } from 'react';

import DataProvider from '../DataProvider';
import * as QueryFetcher from 'components/Explorer/models/query_fetcher';

const useJournalAnalysis = (params, { onFetchStarted, onFetchSuccess, onFetchError } = {}) => {
  const EMPTY_DATASET = { dataset: [] };
  const [dataProvider, setDataProvider] = useState(EMPTY_DATASET);
  const filtersKey = JSON.stringify(params.filters);

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const versionRef = useRef(0);
  const requestRef = useRef(null);
  const callbacksRef = useRef({ onFetchStarted, onFetchSuccess, onFetchError });
  callbacksRef.current = { onFetchStarted, onFetchSuccess, onFetchError };

  const refresh = useMemo(() => {
    return () => {
      if (requestRef.current) requestRef.current.abort();
      const version = ++versionRef.current;
      setDataProvider(EMPTY_DATASET);
      const { onFetchStarted: started, onFetchSuccess: success, onFetchError: fail } = callbacksRef.current;
      if (typeof started === 'function') started();
      const request = QueryFetcher.fetchJSON('journals', paramsRef.current);
      requestRef.current = request;
      request.then((error, response) => {
        if (version !== versionRef.current) return;
        if (error?.name === 'AbortError') return;
        // AbortableRequest yields body=undefined for empty 200 responses;
        // DataProvider would throw on `.length`, so treat that as a fetch
        // failure and route through onFetchError.
        if (!error && Array.isArray(response?.body)) {
          setDataProvider(new DataProvider(response.body));
          if (typeof success === 'function') success(response);
        } else if (typeof fail === 'function') {
          fail(error || new Error('Empty response body'));
        }
      });
    };
  }, [filtersKey]);

  useEffect(() => {
    refresh();
    return () => {
      if (requestRef.current) requestRef.current.abort();
    };
  }, [filtersKey]);

  return { rows: dataProvider.dataset, clear: () => setDataProvider(EMPTY_DATASET), refresh };
};

export { useJournalAnalysis };
